import React from 'react';
import { useTranslation } from 'react-i18next';
import type { InvoicePaymentDetails } from './InvoicePaymentModal.types';
import * as S from './InvoicePaymentModal.styles';
import { formatNumber } from '@/utils/helpers';

function DiscountBadge({
  appearance,
  percent,
}: {
  appearance: 'promo' | 'promotion';
  percent: number | null;
}) {
  if (percent === null) return null;
  return <S.Badge $appearance={appearance}>-{formatNumber(percent)}%</S.Badge>;
}

function TotalPrice({ details }: { details: InvoicePaymentDetails }) {
  const hasPromo = details.showPromoPriceLabel;
  const hasPromotion = details.showPromotionLabel;
  const hasSingleDiscount = hasPromo !== hasPromotion;

  function renderDiscountBadge() {
    if (!hasSingleDiscount) return null;

    if (hasPromo) {
      return (
        <DiscountBadge appearance="promo" percent={details.benefitPercent} />
      );
    }

    return (
      <DiscountBadge
        appearance="promotion"
        percent={details.promotionDiscountPercent}
      />
    );
  }

  return (
    <S.TotalValue>
      {hasSingleDiscount && details.originalPrice !== null && (
        <S.OriginalPrice>{formatNumber(details.originalPrice)}</S.OriginalPrice>
      )}
      {formatNumber(details.price)} ₼{renderDiscountBadge()}
    </S.TotalValue>
  );
}

export function InvoicePaymentSummary({
  details,
}: {
  details: InvoicePaymentDetails;
}) {
  const { t } = useTranslation();
  const hasPromo = details.showPromoPriceLabel;
  const hasPromotion = details.showPromotionLabel;

  return (
    <S.Summary>
      <S.SummaryTitle>{t('pages.cabinet.total_for_pay')}</S.SummaryTitle>
      <S.Rows>
        <S.Row>
          <S.Label>{t('pages.cabinet.package')}</S.Label>
          <S.Value>{details.packageName}</S.Value>
        </S.Row>
        {details.periodDays !== null && (
          <S.Row>
            <S.Label>{t('pages.cabinet.period')}</S.Label>
            <S.Value>
              {t('pages.cabinet.package_period_days', {
                days: details.periodDays,
              })}
            </S.Value>
          </S.Row>
        )}
        {details.activationDate !== null && (
          <S.Row>
            <S.Label>{t('pages.cabinet.activation_date')}</S.Label>
            <S.Value>{details.activationDate}</S.Value>
          </S.Row>
        )}
      </S.Rows>
      <S.Divider />
      {hasPromo && hasPromotion && (
        <>
          <S.Rows>
            <S.Row>
              <S.Label>{t('pages.cabinet.discounted_price')}</S.Label>
              <S.PriceValue>
                {details.originalPrice !== null && (
                  <S.OriginalPrice>
                    {formatNumber(details.originalPrice)}
                  </S.OriginalPrice>
                )}
                {details.promoPrice !== null && (
                  <span>{formatNumber(details.promoPrice)} ₼</span>
                )}
                <DiscountBadge
                  appearance="promo"
                  percent={details.benefitPercent}
                />
              </S.PriceValue>
            </S.Row>
            <S.Row>
              <S.Label>{t('pages.cabinet.promotion')}</S.Label>
              <S.PriceValue>
                {details.promotionDiscountAmount !== null && (
                  <span>
                    -{formatNumber(details.promotionDiscountAmount)} ₼
                  </span>
                )}
                <DiscountBadge
                  appearance="promotion"
                  percent={details.promotionDiscountPercent}
                />
              </S.PriceValue>
            </S.Row>
          </S.Rows>
          <S.Divider />
        </>
      )}
      <S.Row>
        <S.TotalLabel>{t('pages.cabinet.invoice_total')}</S.TotalLabel>
        <TotalPrice details={details} />
      </S.Row>
    </S.Summary>
  );
}
